import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tradesperson } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { debug } from '@/lib/utils';
import { formatPostcodeDisplay, validatePostcode } from '@/lib/postcodeUtils';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, ExternalLink, UserCheck, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hapticPatterns } from '@/lib/haptic';
import { TradePersonCard } from './TradePersonCard';
import { getTradeType, getCustomTradeIconSrc } from '@/lib/tradeUtils';
import { TradeIcon } from '@/components/ui/trade-icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonCard } from '@/components/ui/skeleton-card';
import { useTradeListData, TradeListMode, DataSource } from '@/hooks/useTradeListData';

interface TradeListProps {
  // Core props
  initialLimit?: number;
  mode?: TradeListMode;
  dataSource?: DataSource;
  title?: string;

  // Data source props
  postcodeOrUsername?: string;
  userId?: string;
  providedTradespeople?: Tradesperson[];

  // Filter props
  categoryFilter?: string | null;
  onCategoryChange?: (category: string | null) => void;
  externalCategoryControl?: boolean;
  showCategoryFilter?: boolean;

  // UI customization
  showTitle?: boolean;
  showShareButton?: boolean;

  // Special handling
  highlightedTradesperson?: Tradesperson | null;
  customEmptyState?: React.ReactNode;

  // Callbacks
  onTradespersonSaved?: (trade: Tradesperson) => void;
  onDataLoaded?: (trades: Tradesperson[]) => void;

  // URL sync features
  syncWithUrl?: boolean;

  // Animation control
  refreshTrigger?: number;
}

export function TradeList({
  // Core props with defaults
  initialLimit = 4,
  mode = 'personal',
  dataSource = 'user',
  title = 'My TradesPurple',

  // Data source props
  postcodeOrUsername,
  userId,
  providedTradespeople,

  // Filter props
  categoryFilter = null,
  onCategoryChange,
  externalCategoryControl = false,
  showCategoryFilter = true,

  // UI customization
  showTitle = true,
  showShareButton = false,

  // Special handling
  highlightedTradesperson = null,
  customEmptyState,

  // Callbacks
  onTradespersonSaved,
  onDataLoaded,

  // URL sync features
  syncWithUrl = false,

  // Animation control
  refreshTrigger
}: TradeListProps) {
  // URL and navigation hooks for URL sync feature
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on tradespurple.com domain
  const isTpDomain = typeof window !== 'undefined' &&
                     window.location.hostname.includes('tradespurple.com');

  // Determine base route for navigation
  const homeRoute = isTpDomain ? '/' : '/tradespurple';

  // Get location state to check if this is just a category filter change
  const isFilterNavigation = location?.state?.skipDataReload === true;

  // Auth context for user info
  const { isAuthenticated } = useAuth();

  // Data fetching and state management via custom hook
  const {
    tradespeople,
    filteredTradespeople,
    isLoading,
    error,
    username,
    locationName,
    relationIds,
    tradeNotes,
    categories,
    activeFilter,
    newItemIds,
    handleRemoveTrade,
    handleUndoRemove,
    handleSaveTrade,
    loadTradespeople,
    setActiveFilter,
    setFilteredTradespeople,
  } = useTradeListData({
    dataSource,
    mode,
    postcodeOrUsername,
    userId,
    providedTradespeople,
    categoryFilter,
    refreshTrigger,
    onDataLoaded,
    onTradespersonSaved,
    syncWithUrl,
    isFilterNavigation,
  });

  // UI state
  const [expanded, setExpanded] = useState(false);
  const [limit, setLimit] = useState(Number.MAX_SAFE_INTEGER); // Always show all trades
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Category container ref
  const categoryScrollContainerRef = useRef<HTMLDivElement>(null);

  // Track which card is currently expanded (only one at a time)
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // International postcode validation - used to determine if the parameter is a postcode or username
  const isPostcode = (value: string | undefined) => {
    if (!value) return false;
    return validatePostcode(value);
  };

  // Category scrolling logic
  const checkScrollPosition = useCallback(() => {
    const container = categoryScrollContainerRef.current;
    if (container) {
      // Can scroll left if scrolled right at all
      setCanScrollLeft(container.scrollLeft > 0);

      // Can scroll right if there's more content to scroll to
      setCanScrollRight(
        container.scrollWidth > container.clientWidth &&
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  }, []);

  // Set up scroll event listener for category container
  useEffect(() => {
    const container = categoryScrollContainerRef.current;
    if (!container) return;

    // Check initial scroll position
    checkScrollPosition();

    // Add scroll event listener
    container.addEventListener('scroll', checkScrollPosition);

    // Check again after categories loaded (as content width may change)
    if (categories.length > 0) {
      setTimeout(checkScrollPosition, 100);
    }

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition, categories.length]);

  // Scroll action handlers
  const handleScrollLeft = () => {
    if (categoryScrollContainerRef.current) {
      categoryScrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (categoryScrollContainerRef.current) {
      categoryScrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Category selection handlers
  const handleSelectFilter = (category: string) => {
    if (externalCategoryControl) {
      // If parent component controls the filter
      if (onCategoryChange) {
        // If it's already active, clear it
        if (categoryFilter === category) {
          onCategoryChange(null);
        } else {
          onCategoryChange(category);
        }
      }
    } else {
      // Internal state management
      if (activeFilter === category) {
        clearFilter();
      } else {
        selectFilter(category);
      }
    }
  };

  // Select a filter category (with optional URL sync)
  const selectFilter = (category: string) => {
    if (process.env.NODE_ENV === 'development') {
      debug.log('[TradeList] Filter selection:', { category, activeFilter });
    }

    // Update filtered data immediately to prevent flicker
    const filtered = tradespeople.filter(trade =>
      getTradeType(trade)?.toLowerCase() === category.toLowerCase()
    );
    setFilteredTradespeople(filtered);

    // Set the active filter state
    setActiveFilter(category);

    // URL synchronization (only if enabled and in results mode)
    if (syncWithUrl && mode === 'results' && postcodeOrUsername) {
      // Format the category for URL (lowercase, hyphens instead of spaces)
      const formattedCategory = category.toLowerCase().replace(/\s+/g, '-');

      // Generate the route with the selected category
      const route = isTpDomain
        ? `/${postcodeOrUsername}/${formattedCategory}`
        : `/tradespurple/${postcodeOrUsername}/${formattedCategory}`;

      if (process.env.NODE_ENV === 'development') {
        debug.log('[TradeList] URL Navigation:', {
          from: location.pathname,
          to: route,
          category,
          isTpDomain
        });
      }

      // Navigate with skipDataReload to prevent unnecessary data fetching
      navigate(route, {
        replace: true,
        state: {
          skipDataReload: true,
          previousCategory: activeFilter,
          newCategory: category
        }
      });
    }
  };

  // Clear filter (with optional URL sync)
  const clearFilter = () => {
    if (process.env.NODE_ENV === 'development') {
      debug.log('[TradeList] Clear filter:', { activeFilter });
    }

    // Clear the filter directly
    setActiveFilter(null);

    // Update filtered list to show all tradespeople
    setFilteredTradespeople(tradespeople);

    // URL synchronization (only if enabled and in results mode)
    if (syncWithUrl && mode === 'results' && postcodeOrUsername) {
      // Generate route without category parameter
      const route = isTpDomain
        ? `/${postcodeOrUsername}`
        : `/tradespurple/${postcodeOrUsername}`;

      if (process.env.NODE_ENV === 'development') {
        debug.log('[TradeList] Clear URL Navigation:', {
          from: location.pathname,
          to: route,
          isTpDomain
        });
      }

      // Navigate with skipDataReload
      navigate(route, {
        replace: true,
        state: {
          skipDataReload: true,
          clearedCategory: activeFilter
        }
      });
    }
  };

  // Toggle show more/less list items
  const toggleExpand = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setLimit(tradespeople.length);
    } else {
      setLimit(Number.MAX_SAFE_INTEGER); // Always show all trades
    }
  };

  // Helper to determine card mode based on component mode
  const getCardMode = (): 'saved' | 'public' => {
    switch (mode) {
      case 'personal':
        return 'saved';
      case 'public':
      case 'results':
      case 'search':
        return 'public';
      default:
        return 'public';
    }
  };

  // Handle card expansion toggle - only one card can be expanded at a time
  const handleToggleExpanded = (tradeId: string) => {
    setExpandedCardId(prevId => prevId === tradeId ? null : tradeId);
  };

  // Helper function to determine if an item should animate
  const shouldAnimateItem = (tradeId: string): boolean => {
    return newItemIds.has(tradeId);
  };

  // Generate title text based on mode
  const getTitleText = () => {
    if (title) return title;

    switch (mode) {
      case 'personal':
        return `My TradesPurple${tradespeople.length > 0 ? ` (${tradespeople.length})` : ''}`;
      case 'results':
        if (username) {
          return activeFilter
            ? `${username}'s recommended ${activeFilter}`
            : `${username}'s recommended tradespeople`;
        } else if (postcodeOrUsername && isPostcode(postcodeOrUsername)) {
          return activeFilter
            ? `${activeFilter} near ${formatPostcodeDisplay(postcodeOrUsername)}`
            : `Tradespeople near ${formatPostcodeDisplay(postcodeOrUsername)}`;
        } else {
          return activeFilter
            ? `Popular ${activeFilter}`
            : "Popular tradespeople";
        }
      case 'public':
        return "Community Recommendations";
      case 'search':
        return "Search Results";
      default:
        return "Tradespeople";
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center px-4">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}
        <div className="space-y-3">
          {[...Array(initialLimit)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center px-4">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}
        <div className="mx-4 p-6 bg-white rounded-xl text-purple-900 border-2 border-purple-900 shadow-md">
          <p className="font-bold">{error}</p>
          <div className="mt-4 flex flex-col gap-2">
            <Button
              onClick={() => loadTradespeople()}
              className="bg-purple-700 hover:bg-purple-800 shadow-md"
            >
              Try Again
            </Button>
            {mode === 'results' && (
              <Button
                onClick={() => navigate(homeRoute)}
                variant="outline"
                className="border-2 border-purple-700 text-purple-900 hover:bg-purple-50"
              >
                Back to Search
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (tradespeople.length === 0) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center px-4">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}

        {customEmptyState ? (
          customEmptyState
        ) : (
          mode === 'personal' ? (
            <div className="mx-4 p-6 border-2 border-purple-300 rounded-xl bg-white shadow-sm">
              <div className="text-center">
                <div className="inline-flex rounded-full bg-purple-100 p-3 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-purple-900 mb-2">Your TradesPurple is empty</h2>
                <p className="text-purple-700 mb-4">Start building your list of trusted tradespeople.</p>

                {/* Big prominent Add Trade Button */}
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('tradespurple-action', { detail: { action: 'add' } }))}
                  className="flex items-center justify-center w-full bg-purple-700 hover:bg-purple-800 text-white font-medium py-3 px-4 rounded-lg my-5 shadow-md transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Your First Tradesperson
                </button>
              </div>

              {/* Benefits Section */}
              <div className="mt-8 mb-2">
                <h3 className="text-purple-900 font-semibold text-lg mb-4">Why add tradespeople?</h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">SAVE</p>
                    <p className="text-purple-700 text-sm mt-1">One place to save local trusted trades - no more searching for that handyman's number</p>
                  </div>
                </div>

                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">CONTACT</p>
                    <p className="text-purple-700 text-sm mt-1">The fastest way to message multiple tradespeople at once - we'll even draft it for you</p>
                  </div>
                </div>

                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">SHARE</p>
                    <p className="text-purple-700 text-sm mt-1">Share your list with neighbors and help everyone use the best tradespeople easily</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Card className="mx-4 p-6 text-center border-2 border-purple-900 rounded-xl shadow-md">
              <CardContent>
                {mode === 'results' && !isAuthenticated ? (
                  // Simplified empty state for postcode results
                  <>
                    <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    </div>

                    <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the Pioneer!</h3>

                    <p className="text-purple-700 mb-6 text-lg">
                      No tradespeople have been added in
                      {locationName
                        ? ` the ${locationName} area`
                        : ' this area'} yet.
                    </p>

                    <div className="mb-4">
                      <Button
                        onClick={() => navigate('/auth/signup')}
                        className="bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                      >
                        Add my first tradesperson
                      </Button>
                    </div>

                    <div className="bg-purple-50/70 py-4 px-6 rounded-lg max-w-md mx-auto">
                      <p className="text-purple-700 text-sm">
                        Your recommendations will help others in your community find trusted local trades.
                      </p>
                    </div>
                  </>
                ) : (
                  // Simple, clean empty state that matches the design - same for logged-in and logged-out
                  <>
                    <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    </div>

                    <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the Pioneer!</h3>

                    <p className="text-purple-700 mb-6 text-lg">
                      No tradespeople have been added in
                      {locationName
                        ? ` the ${locationName} area`
                        : ' this area'} yet.
                    </p>

                    <div className="mb-4">
                      <Button
                        onClick={() => isAuthenticated
                          ? window.dispatchEvent(new CustomEvent('tradespurple-action', { detail: { action: 'add' } }))
                          : navigate('/auth/signup')}
                        className="bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                      >
                        {isAuthenticated ? "Add a tradesperson" : "Add my first tradesperson"}
                      </Button>
                    </div>

                    <div className="bg-purple-50/70 py-4 px-6 rounded-lg max-w-md mx-auto">
                      <p className="text-purple-700 text-sm">
                        Your recommendations will help others in your community find trusted local trades.
                      </p>
                    </div>

                    {mode === 'results' && (
                      <Button
                        onClick={() => navigate(homeRoute)}
                        className="bg-purple-700 hover:bg-purple-800 shadow-md px-6 py-2 text-lg mt-4"
                      >
                        Go Back
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 pb-safe safe-inset-bottom">
      {showTitle && (
        <div className="flex justify-between items-center px-4">
          <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>
            {getTitleText()}
          </h2>
        </div>
      )}

      {/* Category Filter - only show when showCategoryFilter is true */}
      {categories.length > 0 && showCategoryFilter && (
        <div className={`${mode === 'results' ? 'mb-3 -mt-3' : 'mb-3 mt-2'}`}>
          {/* For results mode, we integrate with the outer container in ResultsPage */}
          <div className={`relative ${mode === 'results' ? 'bg-white rounded-lg p-2' : ''}`}>
            {/* Category list with navigation arrows */}
            <div className="relative flex items-center">
              {/* Left scroll arrow - only visible when we can scroll left */}
              {canScrollLeft && (
                <button
                  className="absolute left-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-8 h-8 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                  onClick={handleScrollLeft}
                  aria-label="Scroll left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              )}

              {/* Scrollable container - hide scrollbar */}
              <div
                ref={categoryScrollContainerRef}
                className="overflow-x-auto mx-8 no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex items-center py-2 px-1 min-w-full">
                  {/* All category icons */}
                  {categories.map((category) => {
                    const isActive = externalCategoryControl
                      ? categoryFilter === category
                      : activeFilter === category;
                    const customIconSrc = getCustomTradeIconSrc(category);

                    return (
                      <button
                        key={category}
                        onClick={() => {
                          hapticPatterns.light();
                          handleSelectFilter(category);
                        }}
                        title={isActive ? `${category} (tap again to clear)` : category}
                        aria-label={isActive ? `Clear ${category} filter` : `Filter by ${category}`}
                        className={`flex items-center justify-center rounded-full w-16 h-16 flex-shrink-0 mr-4 transition-all touch-scale ${
                          isActive
                            ? 'bg-purple-700 shadow-lg scale-105'
                            : customIconSrc
                              ? 'bg-white hover:bg-purple-50 border-2 border-purple-200 shadow-sm hover:shadow-md hover:border-purple-300'
                              : 'bg-purple-100 hover:bg-purple-200'
                        }`}
                      >
                        <TradeIcon
                          tradeType={category}
                          className={`h-12 w-12 ${isActive ? 'text-white' : 'text-purple-700'}`}
                          categoryButton={true}
                          isActive={isActive}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right scroll arrow - only visible when we can scroll right */}
              {canScrollRight && (
                <button
                  className="absolute right-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-8 h-8 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                  onClick={handleScrollRight}
                  aria-label="Scroll right"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No results when filtered */}
      {filteredTradespeople.length === 0 && (categoryFilter || activeFilter) && (
        <Card className="mx-4 p-6 text-center border-2 border-purple-900 rounded-xl shadow-md">
          <CardContent>
            <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
              <Filter className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>No matches found</h3>
            <p className="text-purple-700 mb-4 text-lg">
              No {externalCategoryControl ? categoryFilter : activeFilter} found. Try a different trade type.
            </p>

            {!isAuthenticated && mode !== 'personal' && (
              <div className="mb-6 mt-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the first to add a {externalCategoryControl ? categoryFilter : activeFilter}</h4>
                <p className="text-purple-700 mb-4">
                  Sign up to add the first {externalCategoryControl ? categoryFilter : activeFilter} in this area and help your neighbours.
                </p>

                <div className="flex justify-center">
                  <Button
                    onClick={() => navigate('/auth/signup')}
                    className="mt-2 bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                  >
                    Add my first tradesperson
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={clearFilter}
              className="bg-purple-700 hover:bg-purple-800 shadow-md px-6 py-2 text-lg"
            >
              Clear Filter
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Highlighted tradesperson section */}
      {filteredTradespeople.length > 0 && highlightedTradesperson && (
        <div className="mb-6">
          <div className="bg-purple-100 p-3 rounded-lg mb-2 border border-purple-300">
            <p className="text-purple-700 text-sm font-medium">
              <UserCheck className="h-4 w-4 mr-1 inline-block" />
              Recommended tradesperson
            </p>
          </div>
          <TradePersonCard
            key={`highlighted-${highlightedTradesperson.id}`}
            trade={highlightedTradesperson}
            mode={getCardMode()}
            postcode={isPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
            onSave={handleSaveTrade}
            notes={mode === 'personal' ? tradeNotes[highlightedTradesperson.id] : undefined}
            relationId={mode === 'personal' ? relationIds[highlightedTradesperson.id] : undefined}
            isExpanded={expandedCardId === highlightedTradesperson.id}
            onToggleExpanded={handleToggleExpanded}
          />

          {filteredTradespeople.length > 0 && (
            <div className="bg-gray-100 p-3 rounded-lg mt-6 mb-2">
              <p className="text-gray-700 text-sm text-center">
                More recommended tradespeople from {username || postcodeOrUsername}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Main tradesperson list */}
      {filteredTradespeople.length > 0 && (
        <div className="space-y-2">
          {/* Recent Tradespeople Section (only for personal mode on logged-in page) */}
          {mode === 'personal' && (
            <>
              {/* Get recent trades from the filtered trades */}
              {(() => {
                // Get recent trades from API call
                const recentTradesIds = new Set(
                  filteredTradespeople
                    .slice(0, 3) // Limit to 3 recent trades
                    .filter(trade => trade.created_at &&
                      new Date(trade.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) // Last 30 days
                    .map(trade => trade.id)
                );

                // If we have recent trades, show them first
                if (recentTradesIds.size > 0) {
                  const recentItems = filteredTradespeople
                    .filter(trade => recentTradesIds.has(trade.id) &&
                      (!highlightedTradesperson || trade.id !== highlightedTradesperson.id));

                  if (recentItems.length > 0) {
                    return (
                      <div>
                        <h3 className="text-sm font-medium text-purple-700 mb-2 px-4">Recently Used</h3>
                        <div className="space-y-2 mb-3">
                          {recentItems.map((trade, index) => (
                            <div key={`recent-${trade.id}`} className={shouldAnimateItem(trade.id) ? "animate-list-item" : ""} style={shouldAnimateItem(trade.id) ? { animationDelay: `${index * 50}ms` } : {}}>
                              <TradePersonCard
                                trade={trade}
                              username={username}
                              mode={getCardMode()}
                              postcode={isPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                              onSave={handleSaveTrade}
                              onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                              onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                              notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                              relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                              isExpanded={expandedCardId === trade.id}
                              onToggleExpanded={handleToggleExpanded}
                            />
                            </div>
                          ))}
                        </div>
                        <h3 className="text-sm font-medium text-purple-700 mb-2 px-4">All Tradespeople</h3>
                      </div>
                    );
                  }
                }

                return null;
              })()}

              {/* Main alphabetical list */}
              {(() => {
                // Filter out the highlighted tradesperson and recent trades
                const recentTradesIds = new Set(
                  filteredTradespeople
                    .slice(0, 3) // Limit to 3 recent trades
                    .filter(trade => trade.created_at &&
                      new Date(trade.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
                    .map(trade => trade.id)
                );

                return filteredTradespeople
                  .filter(trade => {
                    // Skip highlighted
                    if (highlightedTradesperson && trade.id === highlightedTradesperson.id) {
                      return false;
                    }

                    // Skip trades that are already shown in the recent section
                    if (recentTradesIds.has(trade.id)) {
                      return false;
                    }

                    // Include all remaining trades for the main list
                    return true;
                  })
                  .slice(0, limit)
                  .map((trade, index) => (
                    <div key={trade.id} className={shouldAnimateItem(trade.id) ? "animate-list-item" : ""} style={shouldAnimateItem(trade.id) ? { animationDelay: `${index * 50}ms` } : {}}>
                      <TradePersonCard
                        trade={trade}
                      username={username}
                      mode={getCardMode()}
                      postcode={isPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                      onSave={handleSaveTrade}
                      onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                      onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                      notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                      relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                      isExpanded={expandedCardId === trade.id}
                      onToggleExpanded={handleToggleExpanded}
                    />
                    </div>
                  ));
              })()}
            </>
          )}

          {/* All other modes just show the sorted list */}
          {mode !== 'personal' &&
            filteredTradespeople
              // Filter out the highlighted tradesperson if present
              .filter(trade => !highlightedTradesperson || trade.id !== highlightedTradesperson.id)
              .slice(0, limit)
              .map((trade, index) => (
                <div key={trade.id} className={shouldAnimateItem(trade.id) ? "animate-list-item" : ""} style={shouldAnimateItem(trade.id) ? { animationDelay: `${index * 50}ms` } : {}}>
                  <TradePersonCard
                    trade={trade}
                  username={username}
                  mode={getCardMode()}
                  postcode={isPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                  onSave={handleSaveTrade}
                  onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                  onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                  notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                  relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                  isExpanded={expandedCardId === trade.id}
                  onToggleExpanded={handleToggleExpanded}
                />
                </div>
              ))
          }
        </div>
      )}

      {/* Show more/less button - removed for all modes */}
      {/* Keeping this commented out for potential future use
      {filteredTradespeople.length > initialLimit && (
        <Button
          onClick={toggleExpand}
          variant="ghost"
          className="w-full h-12 text-purple-700 hover:text-purple-800 hover:bg-purple-50 rounded-xl flex items-center justify-center"
        >
          {expanded ? (
            <>
              Show less
              <ChevronUp className="h-5 w-5 ml-1.5 stroke-[2.5]" />
            </>
          ) : (
            <>
              Show {filteredTradespeople.length - initialLimit} more
              <ChevronDown className="h-5 w-5 ml-1.5 stroke-[2.5]" />
            </>
          )}
        </Button>
      )} */}

      {/* Share button */}
      {showShareButton && mode !== 'personal' && filteredTradespeople.length > 0 && (
        <div className="mt-8 flex flex-col items-center">
          <Button
            className="bg-purple-700 hover:bg-purple-800 text-white shadow-md py-3 px-6 text-lg rounded-md border-2 border-purple-900"
            onClick={() => {
              // Create a shareable URL that works for current domain
              const shareDomain = isTpDomain ? 'https://tradespurple.com' : 'https://househeld.co/tradespurple';

              // Use the original username case from the database if available
              const displayUsername = username || postcodeOrUsername;

              const shareablePath = `${displayUsername}${activeFilter ? `/${activeFilter.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
              const shareableUrl = `${shareDomain}/${shareablePath}`;

              navigator.clipboard.writeText(shareableUrl);
              toast.success('Link copied to clipboard!');
            }}
          >
            <ExternalLink className="h-5 w-5 mr-2" />
            Share This List
          </Button>
        </div>
      )}
    </div>
  );
}
